import React, { useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { PAGED_REPORTS_VERIFICATION_QUERY, INTERROGATION_SETTINGS_QUERY } from './queries';
import { EXPORT_REPORTS_VERIFICATION_MUTATION } from './mutations';
import { ServiceContext } from 'providers/ServiceProvider';
import { useApolloClient } from '@apollo/client';
import { useQuery, useMutation } from 'hooks/apollo';
import { useTranslation } from 'react-i18next';
import ReportVerificationActions from './ReportVerificationActions';
import { Card, Typography } from '@totalsoft/rocket-ui';
import { CollectionType, getListBrowserStore, useListBrowserUtils } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import ReportVerificationItem from './ReportVerificationItem';
import useReportVerificationHandlers from './useReportVerificationHandlers';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import { buildExportDownloadUrl } from 'features/downloadList/api';
import { useOidcAccessToken } from '@axa-fr/react-oidc';
import { getOidcConfigName } from 'utils/functions';
import { useSimpleError } from 'hooks/apolloWrappers';

export const reportsVerificationStore = getListBrowserStore({
  filterDefinitions: [
    { name: 'method', caption: 'ReportsVerification.Method', type: 'text' },
    { name: 'service', caption: 'ReportsVerification.Service', type: 'select', underlyingType: 'text' },
    { name: 'entityName', caption: 'ReportsVerification.Entity', type: 'text' },
    { name: 'startDate', caption: 'General.ValidFrom', type: 'date' },
    { name: 'endDate', caption: 'General.ValidTo', type: 'date' },
    { name: 'reused', caption: 'ReportsVerification.Reused', type: 'boolean' }
  ],
  orderByFields: [
    { caption: 'ReportsVerification.Method', value: 'Method' },
    { caption: 'ReportsVerification.Service', value: 'Service' },
    { caption: 'ReportsVerification.Entity', value: 'EntityName' },
    { caption: 'ReportsVerification.Data', value: 'CreatedDate' }
  ],
  pager: { orderBy: 'CreatedDate', direction: 'asc' }
});

{
  const data = reportsVerificationStore.get();
  data.hiddenFilters = ['reused'];
  reportsVerificationStore.set(data);
}

const fetchingOptions = {
  queryOptions: {
    query: PAGED_REPORTS_VERIFICATION_QUERY,
    name: 'pagedReportsVerification'
  },
  collectionsOptions: [
    {
      queryOptions: { query: INTERROGATION_SETTINGS_QUERY },
      name: 'interrogationSettings',
      identifier: 'service',
      collectionType: CollectionType.Filter,
      valueKey: 'service',
      labelKey: 'service'
    }
  ]
};

const BASE_COLUMNS = [
  { name: 'id', caption: 'ReportsVerification.Id', width: '100px' },
  { name: 'data', caption: 'ReportsVerification.Data', width: 15 },
  { name: 'user', caption: 'ReportsVerification.User' },
  { name: 'service', caption: 'ReportsVerification.Service' },
  { name: 'entity', caption: 'ReportsVerification.Entity' },
  { name: 'method', caption: 'ReportsVerification.Method', width: 25 }
];

const EXTENDED_COLUMNS = [
  { name: 'orderId', caption: 'ReportsVerification.OrderId' },
  { name: 'documentNumber', caption: 'ReportsVerification.DocumentNumber' },
  { name: 'status', caption: 'ReportsVerification.Status' }
];

const ACTIONS_COLUMN = { name: 'actions', caption: 'actions', width: '120px' };

const getServiceName = filter => (typeof filter === 'string' ? filter : filter?.value) || null;

const ReportVerificationListBrowser = () => {
  const client = useApolloClient();
  const [activeService] = useContext(ServiceContext);
  const { t } = useTranslation();
  const { dataIsProcessing, setDataIsProcessing, handleDownloadSentData, handleDownloadReceivedData } = useReportVerificationHandlers();
  const { updateFilter, store: $store } = useListBrowserUtils(reportsVerificationStore);
  const { accessToken } = useOidcAccessToken(getOidcConfigName());
  const showError = useSimpleError();

  const { data: settingsData, loading: settingsLoading } = useQuery(INTERROGATION_SETTINGS_QUERY);
  const activeServices = useMemo(() => settingsData?.interrogationSettings ?? [], [settingsData]);
  const serviceName = getServiceName($store?.filters?.service);
  const autoSelectedService = useRef(null);

  useEffect(() => {
    if (activeServices.length === 1 && !serviceName) {
      const { service } = activeServices[0];
      autoSelectedService.current = service;
      updateFilter('service')(service);
    }
  }, [activeServices, serviceName, updateFilter]);

  useEffect(() => {
    if (activeServices.length > 1 && serviceName && autoSelectedService.current === serviceName) {
      autoSelectedService.current = null;
      updateFilter('service')(null);
    }
  }, [activeServices, serviceName, updateFilter]);

  const selectedServiceSettings = useMemo(() => activeServices.find(s => s.service === serviceName) || null, [activeServices, serviceName]);

  const showSelectServiceMessage = !settingsLoading && activeServices.length > 1 && !serviceName;
  const showExtendedColumns = selectedServiceSettings?.communicationTypeMethod != null;

  useEffect(() => {
    reportsVerificationStore.update(prev => ({
      ...prev,
      hiddenFilters: showExtendedColumns
        ? prev.hiddenFilters.filter(f => f !== 'reused')
        : [...prev.hiddenFilters.filter(f => f !== 'reused'), 'reused'],
      filters: showExtendedColumns ? prev.filters : { ...prev.filters, reused: prev.initialFilters?.reused }
    }));
  }, [showExtendedColumns]);

  useEffect(() => {
    if (activeService) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedReportsVerification' });
      client.cache.gc();
    }
  }, [activeService, client]);

  const itemComponent = useCallback(
    ({ data }) => (
      <ReportVerificationItem
        data={data}
        onDownloadSentDataFile={handleDownloadSentData}
        onDownloadReceivedDataFile={handleDownloadReceivedData}
        showExtendedColumns={showExtendedColumns}
      />
    ),
    [handleDownloadSentData, handleDownloadReceivedData, showExtendedColumns]
  );

  const columns = useMemo(
    () => (showExtendedColumns ? [...BASE_COLUMNS, ...EXTENDED_COLUMNS, ACTIONS_COLUMN] : [...BASE_COLUMNS, ACTIONS_COLUMN]),
    [showExtendedColumns]
  );

  const [exportReportsVerificationList] = useMutation(EXPORT_REPORTS_VERIFICATION_MUTATION, {
    onCompleted: async ({ exportReportsVerificationList }) => {
      let downloadLink = null;
      try {
        downloadLink = await buildExportDownloadUrl(
          exportReportsVerificationList.filePath,
          exportReportsVerificationList.fileName,
          accessToken
        );
      } catch (error) {
        showError(error);
      }
      if (downloadLink) {
        window.location.href = downloadLink;
      }
    }
  });

  const handleExport = useCallback(
    async exportType => {
      const fileType = exportType.extension === 'xlsx' ? 'excel' : exportType.extension;
      const filters = $store?.filters ?? {};
      await exportReportsVerificationList({
        variables: {
          filters: {
            service: getServiceName(filters.service),
            method: filters.method ?? null,
            entityName: filters.entityName ?? null,
            startDate: filters.startDate ?? null,
            endDate: filters.endDate ?? null,
            reused: filters.reused ?? null,
            fileType
          }
        }
      });
    },
    [$store?.filters, exportReportsVerificationList]
  );

  return (
    <Card icon={ListAltIcon}>
      {showSelectServiceMessage && (
        <Typography variant='subtitle2' fontStyle='italic' color='error' textAlign='center' sx={{ py: 2 }}>
          {t('ReportsVerification.SelectServiceMessage')}
        </Typography>
      )}
      <TableListBrowser name='reports-browser' store={reportsVerificationStore} ItemComponent={itemComponent} columns={columns} />
      <ExportModalComponent store={reportsVerificationStore} exportHandler={handleExport} />
      <ReportVerificationActions dataIsProcessing={dataIsProcessing} setDataIsProcessing={setDataIsProcessing} />
      <ApolloFetchingComponent store={reportsVerificationStore} options={fetchingOptions} />
    </Card>
  );
};

export default ReportVerificationListBrowser;
